"use client"

import type React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { careerApplicationSchema } from "@/lib/validations"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { GradientButton } from "@/components/ui/gradient-button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import {
  Briefcase,
  Send,
  ArrowLeft,
  ArrowRight,
  Users,
  TrendingUp,
  GraduationCap,
  Mail,
} from "lucide-react"
import { toast } from "sonner"
import { Breadcrumb } from "@/components/breadcrumb"
import Link from "next/link"
import { useRecaptcha } from "@/hooks/use-recaptcha"

type CareerApplicationFormData = z.infer<typeof careerApplicationSchema>

const perks = [
  {
    icon: TrendingUp,
    title: "Growth Opportunities",
    description: "Clear career paths with mentorship, training, and internal mobility.",
  },
  {
    icon: Users,
    title: "Collaborative Culture",
    description: "Work alongside a global team that values ideas over hierarchy.",
  },
  {
    icon: GraduationCap,
    title: "Learning & Development",
    description: "Access to industry certifications.",
  },
]

export default function CareersPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CareerApplicationFormData>({
    resolver: zodResolver(careerApplicationSchema),
  })

  const { getToken } = useRecaptcha()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = async (data: CareerApplicationFormData) => {
    setIsSubmitting(true)
    try {
      const recaptchaToken = await getToken("careers_submit")
      if (!recaptchaToken && process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
        toast.error("reCAPTCHA challenge failed. Please try again.")
        setIsSubmitting(false)
        return
      }

      const res = await fetch("/api/forms/careers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, recaptchaToken }),
      })

      const result = await res.json()
      if (!res.ok) throw new Error(result?.error || "Submission failed")

      toast.success("Thank you! Your application has been sent to our HR team.")
      reset()
    } catch (err: any) {
      console.error("Career application submit error", err)
      toast.error(err?.message || "Failed to submit application")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Breadcrumb
        items={[
          { href: "/", label: "Home" },
          { label: "Careers", current: true },
        ]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#518BC4] to-[#7C1414] text-white py-16">
        <div className="container mx-auto px-6">
          <Link
            href="/"
            className="inline-flex items-center text-white/80 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Briefcase className="h-8 w-8" />
            <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
              We're Hiring • Join Our Team
            </Badge>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Careers at ProSoft Electronics</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Help us power the future of electronic components distribution. We're looking for driven
            people who want to grow with a global team and make a real impact.
          </p>
        </div>
      </section>

      {/* Perks */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-6">
            {perks.map((perk, index) => {
              const gradients = [
                "from-[#7C1414] to-[#5A0F0F]",
                "from-slate-900 to-slate-800",
                "from-[#518BC4] to-[#3A6A93]",
                "from-[#7C1414] to-[#518BC4]",
              ]
              const Icon = perk.icon
              return (
                <Card
                  key={perk.title}
                  className={`bg-gradient-to-br ${gradients[index % gradients.length]} text-white border-0 shadow-lg hover:shadow-xl hover:scale-105 transition-all animate-in fade-in slide-in-from-bottom-4 duration-700`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-6 text-center">
                    <Icon className="h-8 w-8 mx-auto mb-4" />
                    <h3 className="text-lg font-bold mb-2">{perk.title}</h3>
                    <p className="text-white/80 text-sm">{perk.description}</p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Application Form and Info */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <Card className="bg-white border border-slate-200 shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-700">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-900">Apply Now</CardTitle>
                <p className="text-slate-600">
                  Fill out the form below and our HR team will get back to you soon.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" id="careers-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">First Name *</label>
                      <Input
                        {...register("firstName")}
                        className="text-lg py-3 border-slate-300 focus:border-[#518BC4]"
                      />
                      {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Last Name *</label>
                      <Input
                        {...register("lastName")}
                        className="text-lg py-3 border-slate-300 focus:border-[#518BC4]"
                      />
                      {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      {...register("email")}
                      className="text-lg py-3 border-slate-300 focus:border-[#518BC4]"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                    <Input
                      type="tel"
                      {...register("phone")}
                      className="text-lg py-3 border-slate-300 focus:border-[#518BC4]"
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Position You're Applying For *</label>
                    <Input
                      {...register("position")}
                      placeholder="e.g. Sales Engineer"
                      className="text-lg py-3 border-slate-300 focus:border-[#518BC4]"
                    />
                    {errors.position && <p className="text-red-500 text-xs mt-1">{errors.position.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      LinkedIn / Portfolio URL
                    </label>
                    <Input
                      {...register("linkedinOrPortfolio")}
                      placeholder="https://linkedin.com/in/yourname"
                      className="text-lg py-3 border-slate-300 focus:border-[#518BC4]"
                    />
                    {errors.linkedinOrPortfolio && (
                      <p className="text-red-500 text-xs mt-1">{errors.linkedinOrPortfolio.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Resume Link</label>
                    <Input
                      {...register("resumeUrl")}
                      placeholder="Link to your resume (Google Drive, Dropbox, etc.)"
                      className="text-lg py-3 border-slate-300 focus:border-[#518BC4]"
                    />
                    {errors.resumeUrl && <p className="text-red-500 text-xs mt-1">{errors.resumeUrl.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Why do you want to join ProSoft Electronics? *
                    </label>
                    <Textarea
                      {...register("coverMessage")}
                      className="text-lg min-h-32 border-slate-300 focus:border-[#518BC4]"
                      placeholder="Tell us about your experience and why you're a great fit..."
                    />
                    {errors.coverMessage && <p className="text-red-500 text-xs mt-1">{errors.coverMessage.message}</p>}
                  </div>

                  <GradientButton type="submit" variant="blue-red" className="w-full group" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit Application"}
                    <Send className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </GradientButton>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Work With Us</h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  At ProSoft Electronics, we're building the future of electronic component distribution.
                  We look for people who are curious, dependable, and excited to solve real problems for
                  our customers across industries.
                </p>
              </div>

              <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
                <CardContent className="p-6 space-y-4">
                  <h3 className="text-2xl font-bold text-slate-900">Have Questions?</h3>
                  <p className="text-slate-600">
                    If you'd rather reach out directly, our HR team is happy to answer any questions
                    about open roles or the application process.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="h-4 w-4" />
                    <a href="mailto:hr@prosoftelectronics.com" className="text-[#518BC4] hover:underline">
                      hr@prosoftelectronics.com
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            Ready to Build Your Career With Us?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
            Submit your application above, or reach out to our HR team directly to learn more about
            open opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            <GradientButton
              variant="red-blue"
              className="group"
              onClick={() => window.open("mailto:hr@prosoftelectronics.com", "_self")}
            >
              Email HR
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </GradientButton>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-slate-900 bg-transparent"
              onClick={() => document.getElementById("careers-form")?.scrollIntoView({ behavior: "smooth" })}
            >
              Apply Now
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
